// routing-v2 — vista agrupada por carrier con multi-prefijo
import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";
import RoutingDetalle from "./RoutingDetalle";

// Los troncales reales se leen de GET /trunks (netvoice.trunks) dentro de CarrierModal.
// Antes habia aca una lista hardcodeada con un solo valor ("linkotel", IP 181.39.8.165 que ya
// no era la de Denwa): obligaba a pasar por "otro..." y de ahi salieron reglas huerfanas.

const PREFIJOS_SUGERIDOS = [
  { prefijo: "02",  desc: "Fijos Quito" },
  { prefijo: "04",  desc: "Fijos Guayaquil" },
  { prefijo: "07",  desc: "Fijos Cuenca" },
  { prefijo: "09",  desc: "Celulares Ecuador" },
  { prefijo: "00",  desc: "Internacional" },
];
// `_X.` ya NO figura acá: la ruta por defecto dejó de ser un prefijo y pasó a ser un switch
// propio (columna `es_default`). Mientras estuvo mezclada con los chips se borró dos veces
// editando la lista — 2026-08-25 (linkotel) y 2026-09-16 (Denwa SS) — y cortó las salientes.

/* Cómo se lee el largo de una regla: "7 díg", "7-9 díg", "≥7 díg", "≤9 díg" o nada. */
function labelLargo({ largo_min, largo_max }) {
  const lo = largo_min ?? null, hi = largo_max ?? null;
  if (lo === null && hi === null) return null;
  if (lo !== null && hi !== null) return lo === hi ? `${lo} díg` : `${lo}-${hi} díg`;
  return lo !== null ? `≥${lo} díg` : `≤${hi} díg`;
}

/* ── Chip de prefijo ─────────────────────────────────────────────────────── */
function PrefixChip({ value, onRemove }) {
  // `value` puede ser un string (forma vieja) o {prefijo, largo_min, largo_max}.
  const item  = typeof value === "string" ? { prefijo: value } : (value || {});
  const pref  = item.prefijo;
  const largo = labelLargo(item);
  const sug = PREFIJOS_SUGERIDOS.find(p => p.prefijo === pref);
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background:"var(--bg-void)", border:"1px solid var(--border)",
      borderRadius:6, padding:"2px 8px", fontSize:12,
      fontFamily:"'JetBrains Mono',monospace", color:"var(--brand)",
    }}>
      {pref}
      {largo && <span style={{ color:"var(--fg)", fontSize:11, opacity:.75 }}> · {largo}</span>}
      {sug && <span style={{ color:"var(--muted)", fontFamily:"inherit", fontSize:11 }}> {sug.desc}</span>}
      {onRemove && (
        <button onClick={onRemove} style={{
          background:"none", border:"none", cursor:"pointer",
          color:"var(--muted)", padding:"0 2px", fontSize:13, lineHeight:1,
        }}>×</button>
      )}
    </span>
  );
}

/* ── Alert ──────────────────────────────────────────────────────────────── */
function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = { success:"nv-alert-ok", error:"nv-alert-err", warn:"nv-alert-warn" }[msg.type] || "nv-alert-ok";
  return (
    <div className={"nv-alert " + cls} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"inherit", fontSize:14 }}>✕</button>
    </div>
  );
}

/* ── Modal carrier (crear / editar) ─────────────────────────────────────── */
function CarrierModal({ carrier, onClose, onSaved }) {
  const isEdit = !!carrier;
  const empty = {
    carrier:"", carrier_ip:"", prioridad:10, activo:true,
    descripcion:"", prefijos:[], es_default:false, es_numeracion:false,
  };
  const [form, setForm] = useState(isEdit ? {
    carrier:     carrier.carrier,
    carrier_ip:  carrier.carrier_ip,
    prioridad:   carrier.prioridad,
    activo:      carrier.activo,
    descripcion: carrier.descripcion || "",
    // El backend ya los manda como {prefijo, largo_min, largo_max}; se tolera la forma vieja.
    prefijos:    (carrier.prefijos || []).map(p => typeof p === "string" ? { prefijo:p } : p),
    es_default:  !!carrier.es_default,
    es_numeracion: !!carrier.es_numeracion,
  } : empty);
  const [newPref, setNewPref] = useState("");
  const [newMin,  setNewMin]  = useState("");
  const [newMax,  setNewMax]  = useState("");
  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);
  // Troncales REALES. El AGI resuelve el endpoint con
  //   JOIN netvoice.trunks t ON LOWER(t.carrier) = LOWER(r.carrier)
  // -> un carrier que no exista como troncal deja reglas que el router DESCARTA en silencio.
  const [troncales, setTroncales] = useState([]);
  // ⚠️ `modo` va SEPARADO de form.carrier a proposito. Antes la condicion de render del input
  // de texto libre era `form.carrier === "otro"`, o sea dependia del mismo estado que el input
  // escribia: al teclear la primera letra el input se DESMONTABA y el nombre no podia pasar de
  // 1 caracter. Asi quedo un carrier llamado "W" en produccion, con 3 reglas que nunca rutearon.
  const [modo, setModo] = useState("lista");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    let vivo = true;
    api.get("/trunks")
      .then(r => { if (vivo) setTroncales(r.data?.data || []); })
      .catch(() => { if (vivo) setTroncales([]); });
    return () => { vivo = false; };
  }, []);

  // Editar una regla cuyo carrier no coincide con ningun troncal (huerfana): se abre en modo
  // texto libre para poder corregir el nombre a mano.
  useEffect(() => {
    if (!troncales.length || !isEdit) return;
    const hay = troncales.some(t => (t.proveedor || "").toLowerCase() === (form.carrier || "").toLowerCase());
    if (!hay) setModo("otro");
  }, [troncales]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarTroncal = (nombre) =>
    troncales.find(t => (t.proveedor || "").toLowerCase() === (nombre || "").trim().toLowerCase());

  const troncalSel = buscarTroncal(form.carrier);
  // Endpoint PJSIP efectivo: endpoint_name si esta cargado, si no derivado del id del troncal
  // ("ca" + primeros 8 hex) — exactamente lo que hace netvoice_router.py.
  const endpointEfectivo = troncalSel
    ? (troncalSel.endpoint_name || ("ca" + String(troncalSel.id || "").replace(/-/g, "").slice(0, 8)))
    : null;

  const handleCarrierSelect = (e) => {
    const v = e.target.value;
    if (v === "otro") { setModo("otro"); set("carrier", ""); set("carrier_ip", ""); return; }
    setModo("lista");
    set("carrier", v);
    const t = buscarTroncal(v);
    set("carrier_ip", t ? (t.host || "") : "");
  };

  const handleNombreLibre = (v) => {
    set("carrier", v);
    const t = buscarTroncal(v);
    set("carrier_ip", t ? (t.host || "") : "");
  };

  const addPrefijo = (pref) => {
    const p = (pref || newPref).trim();
    if (!p) return;
    if (form.prefijos.some(x => x.prefijo === p)) { setNewPref(""); return; }
    const num = (v) => (String(v).trim() === "" ? null : Number(v));
    set("prefijos", [...form.prefijos,
                     { prefijo:p, largo_min:num(newMin), largo_max:num(newMax) }]);
    setNewPref(""); setNewMin(""); setNewMax("");
  };

  const removePrefijo = (p) =>
    set("prefijos", form.prefijos.filter(x => x.prefijo !== p));

  const save = async () => {
    if (!form.carrier.trim()) {
      setErr("Elegi un troncal o escribi el nombre del carrier"); return;
    }
    // Guarda de reglas huerfanas. Solo aplica si la lista de troncales cargo: si GET /trunks
    // fallo no bloqueamos el guardado (el backend valida igual).
    if (troncales.length > 0 && !troncalSel) {
      setErr(`No existe ningun troncal con carrier "${form.carrier.trim()}". Las reglas quedarian huerfanas: el router las descarta en silencio.`);
      return;
    }
    // Un carrier puede quedar sin chips si tiene la ruta por defecto o la de numeración
    // (Denwa SS: una sola regla, la de numeración NetVoice).
    if (form.prefijos.length === 0 && !form.es_default && !form.es_numeracion) {
      setErr("Agrega al menos un prefijo, la ruta por defecto o Números Netvoice"); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const url = `/v1/routing/carrier-group/${encodeURIComponent(carrier.carrier)}`;
        try {
          await api.put(url, form);
        } catch(e) {
          // 409 = el guardia del backend: el guardado eliminaría prefijos que no vienen en
          // la lista. Se le muestra al operador EXACTAMENTE qué se perdería y solo se
          // reintenta si lo confirma. Guardar sin preguntar fue lo que dejó a linkotel sin
          // `_X.`/`09`/`02`/`04` el 2026-08-25 y cortó todas las salientes.
          // El backend puede pedir DOS confirmaciones distintas: `confirmar_borrado` para
          // los prefijos que salen de la lista, y `confirmar_catch_all` APARTE para `_X.`.
          // Se piden de a una, cada una con su texto: un OK dado para sacar `03` no debe
          // llevarse el catch-all de arrastre, que es como se cortaron las salientes el
          // 2026-08-25 (linkotel) y otra vez el 2026-09-16 (Denwa SS).
          let flags = {};
          let pendiente = e;
          for (let intento = 0; intento < 2 && pendiente; intento++) {
            const detalle = pendiente.response?.data?.detail;
            if (pendiente.response?.status !== 409 || !detalle) throw pendiente;
            if (!(await confirmar(detalle + "\n\n¿Continuar de todas formas?"))) {
              setErr("Guardado cancelado: no se modificó ninguna regla.");
              return;
            }
            flags = detalle.includes("confirmar_catch_all")
              ? { ...flags, confirmar_catch_all: true }
              : { ...flags, confirmar_borrado: true };
            try {
              await api.put(url, { ...form, ...flags });
              pendiente = null;
            } catch(e2) { pendiente = e2; }
          }
          if (pendiente) throw pendiente;
        }
      } else {
        await api.post("/v1/routing/carrier-group", form);
      }
      onSaved();
    } catch(e) {
      setErr(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="nv-modal-backdrop" onClick={e => e.target===e.currentTarget && onClose()}>
      {dialogoConfirm}
      <div className="nv-modal" style={{ maxWidth:580 }}>
        <div className="nv-modal-header">
          <h3 style={{ margin:0, fontSize:15 }}>
            {isEdit ? `Editar carrier — ${carrier.carrier}` : "Nuevo carrier"}
          </h3>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="nv-modal-body" style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {err && (
            <div className="nv-alert nv-alert-err">
              <span>{err}</span>
              <button onClick={()=>setErr(null)} style={{background:"none",border:"none",cursor:"pointer",color:"inherit"}}>✕</button>
            </div>
          )}

          {/* Carrier (troncales reales) + destino efectivo */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <label className="nv-field">
              <span>Carrier <small style={{color:"var(--muted)"}}>(troncal)</small></span>
              <select className="nv-input" value={modo === "otro" ? "otro" : form.carrier}
                      onChange={handleCarrierSelect}>
                <option value="">— seleccionar —</option>
                {troncales.map(t => (
                  <option key={t.id} value={t.proveedor}>
                    {t.proveedor} — {t.host}{t.activo === "no" ? " (inactivo)" : ""}
                  </option>
                ))}
                <option value="otro">otro…</option>
              </select>
            </label>
            <label className="nv-field">
              <span>Destino real <small style={{color:"var(--muted)"}}>(del troncal, solo lectura)</small></span>
              <input className="nv-input" readOnly disabled
                value={troncalSel ? `${troncalSel.host} · endpoint ${endpointEfectivo}` : "— sin troncal que coincida —"}/>
            </label>
          </div>

          {modo === "otro" && (
            <label className="nv-field">
              <span>Nombre del carrier</span>
              <input className="nv-input" value={form.carrier}
                onChange={e=>handleNombreLibre(e.target.value)}
                placeholder="tiene que coincidir con el carrier de un troncal"/>
              {form.carrier.trim() && !troncalSel && troncales.length > 0 && (
                <small style={{ color:"var(--danger, #c0392b)" }}>
                  No hay ningun troncal con ese carrier — las reglas quedarian huerfanas.
                </small>
              )}
            </label>
          )}

          {/* Prioridad + Activo */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <label className="nv-field">
              <span>Prioridad <small style={{color:"var(--muted)"}}>(1 = mayor prioridad)</small></span>
              <input className="nv-input" type="number" min={1} max={100}
                value={form.prioridad} onChange={e=>set("prioridad",parseInt(e.target.value)||10)}/>
            </label>
            <label className="nv-field" style={{ justifyContent:"flex-end" }}>
              <span>Estado</span>
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <input type="checkbox" checked={form.activo} onChange={e=>set("activo",e.target.checked)}/>
                <span style={{ color: form.activo ? "var(--success)" : "var(--muted)" }}>
                  {form.activo ? "Activo" : "Inactivo"}
                </span>
              </label>
            </label>
          </div>

          {/* Descripción */}
          <label className="nv-field">
            <span>Descripción <small style={{color:"var(--muted)"}}>(opcional)</small></span>
            <input className="nv-input" value={form.descripcion}
              onChange={e=>set("descripcion",e.target.value)}
              placeholder="ej: Carrier principal Ecuador"/>
          </label>

          {/* Prefijos — chips */}
          <div>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:8, color:"var(--fg)" }}>
              Prefijos de destino
              <small style={{ color:"var(--muted)", fontWeight:400, marginLeft:6 }}>
                ({form.prefijos.length} configurados)
              </small>
            </div>

            {/* Chips actuales */}
            {form.prefijos.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10,
                            padding:10, background:"var(--bg-void)", borderRadius:6,
                            border:"1px solid var(--border)" }}>
                {form.prefijos.map(p => (
                  <PrefixChip key={p.prefijo} value={p}
                              onRemove={() => removePrefijo(p.prefijo)}/>
                ))}
              </div>
            )}

            {/* Input manual: prefijo + largo. El largo es lo que permite escribir "la serie
                602, de 7 dígitos" — antes sólo existía el prefijo y el match era por
                comienzo, así que `6` se comía todo lo que empezara con 6. */}
            <div style={{ display:"flex", gap:8 }}>
              <input className="nv-input" style={{ flex:1 }}
                value={newPref}
                onChange={e=>setNewPref(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addPrefijo(); }}}
                placeholder="Prefijo (ej. 602)"/>
              <input className="nv-input" style={{ width:110 }} type="number" min="1"
                value={newMin} onChange={e=>setNewMin(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addPrefijo(); }}}
                placeholder="largo desde"/>
              <input className="nv-input" style={{ width:110 }} type="number" min="1"
                value={newMax} onChange={e=>setNewMax(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addPrefijo(); }}}
                placeholder="largo hasta"/>
              <button className="nv-btn nv-btn-ghost"
                onClick={()=>addPrefijo()} disabled={!newPref.trim()}>
                + Agregar
              </button>
            </div>
            <small style={{ color:"var(--muted)", fontSize:11, display:"block", marginTop:6 }}>
              El largo es en dígitos, sobre el número marcado. Vacío = cualquier largo. Para una
              serie exacta poné el mismo valor en las dos (ej. <code>602</code> · desde 7 · hasta 7).
            </small>
          </div>

          {/* Ruta por defecto — NO es un prefijo */}
          <div style={{ marginTop:16, padding:12, background:"var(--bg-void)",
                        border:"1px solid var(--border)", borderRadius:6 }}>
            <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
              <input type="checkbox" checked={!!form.es_default}
                     onChange={e=>set("es_default", e.target.checked)}
                     style={{ marginTop:3 }}/>
              <span>
                <span style={{ fontSize:12, fontWeight:600, color:"var(--fg)" }}>
                  Ruta por defecto de este carrier
                </span>
                <small style={{ color:"var(--muted)", fontSize:11, display:"block", marginTop:2 }}>
                  Se usa cuando el destino no matchea ningún prefijo de ningún carrier. Sin
                  ninguna ruta por defecto, esos destinos no salen. Es una fila aparte: no se
                  borra al editar los prefijos de arriba.
                </small>
              </span>
            </label>
          </div>

          {/* Numeración NetVoice — tampoco es un prefijo */}
          <div style={{ marginTop:8, padding:12, background:"var(--bg-void)",
                        border:"1px solid var(--border)", borderRadius:6 }}>
            <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
              <input type="checkbox" checked={!!form.es_numeracion}
                     onChange={e=>set("es_numeracion", e.target.checked)}
                     style={{ marginTop:3 }}/>
              <span>
                <span style={{ fontSize:12, fontWeight:600, color:"var(--fg)" }}>
                  Números Netvoice
                </span>
                <small style={{ color:"var(--muted)", fontSize:11, display:"block", marginTop:2 }}>
                  Cubre todo destino fijo que cae en una serie de Series Numéricas (con el área
                  del que marca para los locales de 7 dígitos) y no contestó en Eneural. Gana a
                  cualquier prefijo. Es una fila aparte, como la ruta por defecto.
                </small>
              </span>
            </label>
          </div>
        </div>

        <div className="nv-modal-footer">
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="nv-btn nv-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear carrier"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal probar número ─────────────────────────────────────────────────── */
function TestModal({ onClose }) {
  const [number, setNumber] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const test = async () => {
    if (!number.trim()) return;
    setLoading(true);
    try {
      const r = await api.post(`/v1/routing/test?number=${encodeURIComponent(number)}`);
      setResult(r.data);
    } catch(e) {
      setResult({ error: e.response?.data?.detail || "Error" });
    } finally { setLoading(false); }
  };

  return (
    <div className="nv-modal-backdrop" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="nv-modal" style={{ maxWidth:440 }}>
        <div className="nv-modal-header">
          <h3 style={{ margin:0, fontSize:15 }}>🧪 Probar número</h3>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="nv-modal-body" style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <p style={{ margin:0, color:"var(--muted)", fontSize:13 }}>
            Ingresa un número de destino y verás qué carrier se usaría.
          </p>
          <div style={{ display:"flex", gap:8 }}>
            <input className="nv-input" style={{ flex:1 }} value={number}
              onChange={e=>setNumber(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&test()}
              placeholder="ej: 0998765432 / 021234567 / 001800…" autoFocus/>
            <button className="nv-btn nv-btn-primary" onClick={test}
              disabled={loading||!number.trim()}>
              {loading ? "…" : "Probar"}
            </button>
          </div>
          {result && (
            <div style={{ background:"var(--bg-void)", borderRadius:8, padding:14, fontSize:13 }}>
              {result.error ? (
                <span style={{ color:"var(--danger)" }}>{result.error}</span>
              ) : result.matched ? (
                <>
                  <div style={{ marginBottom:8 }}>
                    <span className="nv-badge-ok" style={{ marginRight:8 }}>✓ Ruta encontrada</span>
                  </div>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    {[
                      ["Número",    result.number],
                      ["Regla",     result.rule?.nombre],
                      ["Prefijo",   result.rule?.prefijo],
                      ["Carrier",   result.rule?.carrier],
                      ["IP Carrier",result.rule?.carrier_ip],
                      ["Prioridad", result.rule?.prioridad],
                    ].map(([k,v])=>(
                      <tr key={k}>
                        <td style={{ color:"var(--muted)", paddingRight:12, paddingBottom:4, width:90 }}>{k}</td>
                        <td style={{ fontFamily:"'JetBrains Mono',monospace", color:"var(--brand)" }}>{v}</td>
                      </tr>
                    ))}
                  </table>
                </>
              ) : (
                <span style={{ color:"var(--danger)" }}>⚠ Sin ruta — número no coincide con ninguna regla activa</span>
              )}
            </div>
          )}
        </div>
        <div className="nv-modal-footer">
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ────────────────────────────────────────────────── */
export default function Routing() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [alert, setAlert]       = useState(null);
  const [modal, setModal]       = useState(null); // null | {type:"carrier",carrier} | {type:"test"}
  const [deleting, setDeleting] = useState(null);
  // Carrier cuyo panel de discado está desplegado (uno a la vez).
  const [discado, setDiscado]   = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/v1/routing/carriers");
      setCarriers(Array.isArray(r.data) ? r.data : []);
    } catch(e) {
      setAlert({ type:"error", text:"Error cargando carriers: " + (e.response?.data?.detail || e.message) });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // `window.confirm` devuelve false sin abrir nada en este navegador: ver ConfirmDialog.js.
  const [confirmar, dialogoConfirm] = useConfirm();

  const handleSaved = () => {
    setModal(null);
    setAlert({ type:"success", text:"Carrier guardado correctamente" });
    load();
  };

  const handleDelete = async (carrierName) => {
    if (!(await confirmar(`¿Eliminar carrier "${carrierName}" y todos sus prefijos?`))) return;
    setDeleting(carrierName);
    const url = `/v1/routing/carrier-group/${encodeURIComponent(carrierName)}`;
    try {
      try {
        await api.delete(url);
      } catch(e) {
        // 409 = el carrier tiene el catch-all `_X.`: borrarlo corta TODAS las salientes.
        const detalle = e.response?.data?.detail;
        if (e.response?.status !== 409 || !detalle) throw e;
        if (!(await confirmar(detalle + "\n\n¿Continuar de todas formas?"))) {
          setAlert({ type:"info", text:"Eliminación cancelada: no se modificó ninguna regla." });
          return;
        }
        await api.delete(url + "?confirmar_catch_all=true");
      }
      setAlert({ type:"success", text:`Carrier ${carrierName} eliminado` });
      load();
    } catch(e) {
      setAlert({ type:"error", text:"Error al eliminar: " + (e.response?.data?.detail || e.message) });
    } finally { setDeleting(null); }
  };

  const toggleActivo = async (c) => {
    try {
      await api.put(`/v1/routing/carrier-group/${encodeURIComponent(c.carrier)}`, {
        ...c, activo: !c.activo,
      });
      load();
    } catch(e) {
      // Antes era un "Error al cambiar estado" a secas, que se comía el 409 del
      // guardia: el operador veía un genérico sin enterarse de que el guardado iba a
      // borrar prefijos.
      setAlert({ type:"error",
                 text:"Error al cambiar estado: " + (e.response?.data?.detail || e.message) });
    }
  };

  return (
    <div style={{ padding:"24px 28px", maxWidth:1200 }}>
      {dialogoConfirm}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, fontWeight:600 }}>Grupos de Rutas</h2>
          <p style={{ margin:"4px 0 0", color:"var(--muted)", fontSize:13 }}>
            Un carrier puede tener múltiples prefijos. Orden: prefijo más largo primero, luego mayor prioridad.
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="nv-btn nv-btn-ghost" onClick={()=>setModal({type:"test"})}>
            🧪 Probar número
          </button>
          <button className="nv-btn nv-btn-primary" onClick={()=>setModal({type:"carrier",carrier:null})}>
            + Nuevo carrier
          </button>
        </div>
      </div>

      <Alert msg={alert} onClose={()=>setAlert(null)}/>

      {/* Tabla de carriers */}
      <div className="nv-card" style={{ padding:0, overflow:"hidden" }}>
        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:"var(--muted)" }}>Cargando…</div>
        ) : carriers.length === 0 ? (
          <div style={{ padding:40, textAlign:"center", color:"var(--muted)" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📭</div>
            <div>No hay carriers configurados</div>
            <button className="nv-btn nv-btn-primary" style={{ marginTop:16 }}
              onClick={()=>setModal({type:"carrier",carrier:null})}>
              Crear primer carrier
            </button>
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--border)", color:"var(--muted)", textAlign:"left" }}>
                <th style={{ padding:"10px 16px" }}>Carrier</th>
                <th style={{ padding:"10px 12px" }}>IP / Host</th>
                <th style={{ padding:"10px 12px" }}>Prefijos</th>
                <th style={{ padding:"10px 12px", textAlign:"center" }}>Prioridad</th>
                <th style={{ padding:"10px 12px", textAlign:"center" }}>Estado</th>
                <th style={{ padding:"10px 12px", textAlign:"right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map(c => (
                <React.Fragment key={c.carrier}>
                <tr style={{ borderBottom:"1px solid var(--border)", opacity: c.activo ? 1 : 0.5 }}>

                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ fontWeight:600, color:"var(--fg)" }}>{c.carrier}</div>
                    {c.descripcion && (
                      <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>{c.descripcion}</div>
                    )}
                  </td>

                  <td style={{ padding:"12px 12px" }}>
                    <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:"var(--muted)" }}>
                      {c.carrier_ip}
                    </span>
                  </td>

                  <td style={{ padding:"12px 12px" }}>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {/* Con las rutas de Denwa un carrier tiene cientos de prefijos: se muestran los
                          primeros y el resto se ve en «Rutas y traducciones». */}
                      {[...c.prefijos]
                        .sort((a, b) => (a.prefijo || a).length - (b.prefijo || b).length
                                        || String(a.prefijo || a).localeCompare(String(b.prefijo || b)))
                        .slice(0, 12).map(p => (
                        <PrefixChip key={typeof p === "string" ? p : p.prefijo} value={p}/>
                      ))}
                      {c.prefijos.length > 12 && (
                        <button className="nv-badge-muted" style={{ fontSize:11, border:"none", cursor:"pointer" }}
                          onClick={()=>setDiscado(c.carrier)}>
                          +{c.prefijos.length - 12} más
                        </button>
                      )}
                      {c.es_numeracion && (
                        <span className="nv-badge-muted" style={{ fontSize:11 }}
                              title="Destinos en las series de NetVoice (did_ranges) que no están activos en Eneural">
                          Números Netvoice
                        </span>
                      )}
                      {c.es_default && (
                        <span className="nv-badge-muted" style={{ fontSize:11 }}
                              title="Se usa cuando ningún prefijo matchea">
                          ruta por defecto
                        </span>
                      )}
                    </div>
                  </td>

                  <td style={{ padding:"12px 12px", textAlign:"center" }}>
                    <span className="nv-badge-muted">{c.prioridad}</span>
                  </td>

                  <td style={{ padding:"12px 12px", textAlign:"center" }}>
                    <button onClick={()=>toggleActivo(c)}
                      style={{ background:"none", border:"none", cursor:"pointer",
                               fontSize:12, padding:"2px 8px", borderRadius:4 }}
                      className={c.activo ? "nv-badge-ok" : "nv-badge-muted"}>
                      {c.activo ? "Activo" : "Inactivo"}
                    </button>
                  </td>

                  <td style={{ padding:"12px 12px", textAlign:"right" }}>
                    <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                      {/* La modificación de discado es una propiedad del carrier → se despliega acá */}
                      <button className="nv-btn nv-btn-ghost"
                        style={{ padding:"4px 10px", fontSize:12 }}
                        title="Rutas y reglas de traducción de este carrier, como en Denwa SS"
                        onClick={()=>setDiscado(d => d === c.carrier ? null : c.carrier)}>
                        {discado === c.carrier ? "▾" : "▸"} Rutas y traducciones
                      </button>
                      <button className="nv-btn nv-btn-ghost"
                        style={{ padding:"4px 10px", fontSize:12 }}
                        onClick={()=>setModal({type:"carrier", carrier:c})}>
                        Editar
                      </button>
                      <button className="nv-btn"
                        style={{ padding:"4px 10px", fontSize:12,
                                 background:"var(--danger)", color:"#fff",
                                 border:"none", borderRadius:4, cursor:"pointer" }}
                        onClick={()=>handleDelete(c.carrier)}
                        disabled={deleting===c.carrier}>
                        {deleting===c.carrier ? "…" : "Eliminar"}
                      </button>
                    </div>
                  </td>
                </tr>

                {discado === c.carrier && (
                  <tr>
                    <td colSpan={6} style={{ padding:0, borderBottom:"1px solid var(--border)" }}>
                      <RoutingDetalle carrier={c.carrier} carrierIp={c.carrier_ip} onChanged={load}/>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info */}
      <div style={{ marginTop:16, padding:14, background:"var(--bg-surface)", borderRadius:8,
                    fontSize:12, color:"var(--muted)", lineHeight:1.6 }}>
        <strong style={{ color:"var(--fg)" }}>Cómo funciona:</strong>{" "}
        Cada carrier agrupa todos sus prefijos de destino. Al marcar una llamada, Asterisk
        evalúa el prefijo más largo primero y luego por prioridad. El prefijo{" "}
        <code>_X.</code> actúa como catch-all. Los cambios aplican inmediatamente.
      </div>

      {/* Modales */}
      {modal?.type === "carrier" && (
        <CarrierModal
          carrier={modal.carrier}
          onClose={()=>setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {modal?.type === "test" && (
        <TestModal onClose={()=>setModal(null)}/>
      )}
    </div>
  );
}
